import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Users, FolderKanban, Wrench, FileText, Star, FileSignature, Briefcase, Truck, ShoppingCart, Package, CreditCard, Hash } from "lucide-react";
import CommandInput from "@/components/cmdk/CommandInput";
import { ResultsHeader, ResultRow, QuickLinkRow, EmptyResults, FooterHints } from "@/components/cmdk/CommandRows";
import { useCmdKHotkey, useAdminSearch, useArrowNav } from "@/components/cmdk/hooks";
import { useState } from "react";

const SECTIONS = [
  { key: "clients", icon: Users, label: "Clients", path: () => "/admin/clients", primary: (i) => i.name, secondary: (i) => `${i.customer_code || i.customer_number || ""} · ${i.email || ""}` },
  { key: "projects", icon: FolderKanban, label: "Projects", path: (i) => `/admin/projects/${i.project_id}`, primary: (i) => i.title, secondary: (i) => `${i.project_number || i.customer_code || ""} · ${i.status || ""}` },
  { key: "work_orders", icon: Wrench, label: "Work Orders", path: () => "/admin/work-orders", primary: (i) => i.title, secondary: (i) => `${i.work_order_number || i.customer_code || ""} · ${i.status || ""}` },
  { key: "invoices", icon: FileText, label: "Invoices", path: () => "/admin/payments", primary: (i) => i.title, secondary: (i) => `${i.invoice_number || i.customer_code || ""} · $${(i.amount || 0).toFixed(2)} · ${i.status || ""}` },
  { key: "estimates", icon: FileSignature, label: "Quotes", path: (i) => `/admin/estimates/${i.estimate_id}`, primary: (i) => i.title, secondary: (i) => `${i.customer_code || ""} · $${(i.grand_total || 0).toFixed(2)} · ${i.status || ""}` },
  { key: "payments", icon: CreditCard, label: "Payments", path: () => "/admin/finances", primary: (i) => `$${(i.amount || 0).toFixed(2)} ${i.currency || "USD"}`, secondary: (i) => `${i.payment_status || ""} · ${i.customer_email || i.session_id || ""}` },
  { key: "employees", icon: Briefcase, label: "Employees", path: () => "/admin/employees", primary: (i) => i.name, secondary: (i) => `${i.employee_number || i.employee_code || ""} · ${i.role || ""}` },
  { key: "suppliers", icon: Truck, label: "Suppliers", path: () => "/admin/suppliers", primary: (i) => i.name, secondary: (i) => `${i.contact_name || ""} · ${i.contact_email || ""}` },
  { key: "materials", icon: Package, label: "Inventory / SKU", path: () => "/admin/materials", primary: (i) => i.name, secondary: (i) => `SKU ${i.sku || "—"} · ${i.stock || 0} on hand · ${i.reserved || 0} reserved` },
  { key: "purchase_orders", icon: ShoppingCart, label: "Purchase Orders", path: () => "/admin/purchase-orders", primary: (i) => i.po_number, secondary: (i) => `${i.supplier_name || ""} · ${i.status || ""} · $${(i.total || 0).toFixed(2)}` },
  { key: "serials", icon: Hash, label: "Serial numbers", path: () => "/admin/materials", primary: (i) => i.serial, secondary: (i) => `${i.material_name || ""} · SKU ${i.sku || ""}` },
  { key: "testimonials", icon: Star, label: "Testimonials", path: () => "/admin/testimonials", primary: (i) => `${i.user_name} — ${i.rating}/5`, secondary: (i) => (i.text || "").slice(0, 80) },
];

const QUICK_LINKS = [
  { label: "Go to Overview", path: "/admin", k: "overview" },
  { label: "Estimates pipeline", path: "/admin/estimates", k: "estimates" },
  { label: "Site Settings", path: "/admin/settings", k: "settings" },
];

// Navigation index — every admin destination reachable by page name / synonym.
// Ordered by frequency of use. Keywords let common English words ("customer",
// "money", "reorder") resolve without the user knowing the internal label.
const NAV_INDEX = [
  { label: "Today",             path: "/admin/today",               keywords: "today dashboard command center overview home",              icon: FolderKanban },
  { label: "Clients",           path: "/admin/clients",             keywords: "clients client customers customer contacts crm",             icon: Users },
  { label: "Pipeline",          path: "/admin/pipeline",            keywords: "pipeline sales leads funnel",                                icon: FolderKanban },
  { label: "Requests",          path: "/admin/requests",            keywords: "requests service quote inquiries leads",                     icon: FileText },
  { label: "Projects",          path: "/admin/projects",            keywords: "projects project jobs installations work",                   icon: FolderKanban },
  { label: "Project stages",    path: "/admin/project-stages",      keywords: "stages board kanban pipeline projects",                      icon: FolderKanban },
  { label: "Dispatch",          path: "/admin/dispatch",            keywords: "dispatch schedule crew assign",                              icon: Truck },
  { label: "Materials · Inventory", path: "/admin/materials",       keywords: "materials inventory stock sku reorder",                      icon: Package },
  { label: "Inventory forecast",path: "/admin/inventory-forecast",  keywords: "inventory forecast reorder burn stockout",                   icon: Package },
  { label: "Purchase orders",   path: "/admin/purchase-orders",     keywords: "purchase orders po suppliers procurement",                   icon: ShoppingCart },
  { label: "Suppliers",         path: "/admin/suppliers",           keywords: "suppliers vendors vendor procurement",                       icon: Truck },
  { label: "Invoices",          path: "/admin/payments",            keywords: "invoices invoice billing money ar payments",                 icon: FileText },
  { label: "Payments",          path: "/admin/payments",            keywords: "payments payment collections money cash",                    icon: CreditCard },
  { label: "Finances",          path: "/admin/finances",            keywords: "finances finance accounting books money ledger",             icon: CreditCard },
  { label: "Profit & Loss",     path: "/admin/pnl",                 keywords: "pnl profit loss income statement report",                    icon: FileText },
  { label: "Journal entries",   path: "/admin/journal-entries",     keywords: "journal ledger entries accounting book",                     icon: FileText },
  { label: "Bank reconciliation", path: "/admin/bank-recon",        keywords: "bank reconciliation match statements",                       icon: CreditCard },
  { label: "Chart of accounts", path: "/admin/chart-of-accounts",   keywords: "chart accounts coa gl ledger",                               icon: FileText },
  { label: "Project profit",    path: "/admin/project-profit",      keywords: "project profit margin job costing",                          icon: FileText },
  { label: "Turnover tax",      path: "/admin/tot-report",          keywords: "tot turnover tax vat st maarten",                            icon: FileText },
  { label: "Reports",           path: "/admin/reports",             keywords: "reports analytics kpi export",                               icon: FileText },
  { label: "Executive",         path: "/admin/executive",           keywords: "executive summary owner kpis",                               icon: FolderKanban },
  { label: "Approvals",         path: "/admin/approvals",           keywords: "approvals approve pending review",                           icon: FolderKanban },
  { label: "Data integrity",    path: "/admin/data-integrity",      keywords: "data integrity audit checks",                                 icon: FileText },
  { label: "Client messages",   path: "/admin/project-messages",    keywords: "messages chat client inbox threads",                          icon: FileText },
  { label: "Quotes",            path: "/admin/estimates",           keywords: "quotes estimates proposals",                                 icon: FileSignature },
  { label: "Packages",          path: "/admin/packages",            keywords: "packages plans bundles",                                     icon: Package },
  { label: "Expansion requests",path: "/admin/expansions",          keywords: "expansion upgrade addon requests",                           icon: FolderKanban },
  { label: "Employees",         path: "/admin/employees",           keywords: "employees staff team crew hr",                                icon: Briefcase },
  { label: "Timesheets",        path: "/admin/timesheets",          keywords: "timesheets time logs hours labor",                           icon: Briefcase },
  { label: "Leave",             path: "/admin/leave",               keywords: "leave time off pto vacation",                                icon: Briefcase },
  { label: "Payroll",           path: "/admin/payroll",             keywords: "payroll wages salaries",                                     icon: Briefcase },
  { label: "Testimonials",      path: "/admin/testimonials",        keywords: "testimonials reviews ratings",                               icon: Star },
  { label: "Emails",            path: "/admin/emails",              keywords: "emails email outbox transactional",                          icon: FileText },
  { label: "Audit log",         path: "/admin/audit",               keywords: "audit log activity trail",                                   icon: FileText },
  { label: "Settings",          path: "/admin/settings",            keywords: "settings site config preferences",                           icon: FileText },
  { label: "Access control",    path: "/admin/access-control",      keywords: "access control roles permissions rbac",                      icon: FolderKanban },
  { label: "Sales pipeline",    path: "/admin/sales-pipeline",      keywords: "sales pipeline leads deals",                                 icon: FolderKanban },
];

export default function CommandPalette() {
  const [open, setOpen] = useCmdKHotkey();
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { results, loading } = useAdminSearch(q, open);

  // Focus input when opened, reset query when closed
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setQ("");
  }, [open]);

  // Build flat navigable list (search hits, or quick links if no query)
  const navHits = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return NAV_INDEX.filter(
      (n) => n.label.toLowerCase().includes(term) || n.keywords.includes(term)
    ).slice(0, 8);
  }, [q]);

  const flat = useMemo(() => {
    const out = [];
    if (q.trim()) {
      navHits.forEach((n) => out.push({ nav: n }));
      SECTIONS.forEach((s) => (results[s.key] || []).forEach((item) => out.push({ section: s, item })));
    } else {
      QUICK_LINKS.forEach((qk) => out.push({ quick: qk }));
    }
    return out;
  }, [q, results, navHits]);

  const onSelect = (idx) => {
    const entry = flat[idx];
    if (!entry) return;
    const dest = entry.quick ? entry.quick.path
               : entry.nav   ? entry.nav.path
               : entry.section.path(entry.item);
    navigate(dest);
    setOpen(false);
  };

  const [activeIdx, setActiveIdx] = useArrowNav(open, flat.length, onSelect);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-start pt-[10vh] bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="command-palette">
      <div className="w-full max-w-2xl mx-4 bg-white dark:bg-slate-900 dark:border dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <CommandInput ref={inputRef} value={q} onChange={setQ} loading={loading} onClose={() => setOpen(false)} />

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {!q.trim() && <ResultsHeader label="Quick navigation" />}
          {flat.length === 0 && q.trim() && !loading && <EmptyResults q={q} />}
          {q.trim() ? (
            <>
              {navHits.length > 0 && (
                <div className="mb-1">
                  <ResultsHeader label="Jump to" />
                  {navHits.map((n, i) => {
                    const fi = i; // nav hits come first in flat[]
                    return (
                      <QuickLinkRow
                        key={`nav-${n.path}`}
                        label={n.label}
                        active={fi === activeIdx}
                        onHover={() => setActiveIdx(fi)}
                        onSelect={() => onSelect(fi)}
                        testId={`cmdk-nav-${n.path.replace(/[^a-z]/gi, "-")}`}
                      />
                    );
                  })}
                </div>
              )}
              {SECTIONS.map((s) => {
              const items = results[s.key] || [];
              if (!items.length) return null;
              return (
                <div key={s.key} className="mb-1">
                  <ResultsHeader icon={s.icon} label={s.label} />
                  {items.map((item, idxInSec) => {
                    const fi = flat.findIndex((f) => f.section?.key === s.key && f.item === item);
                    const id = item.user_id || item.project_id || item.work_order_id || item.invoice_id || item.estimate_id || item.testimonial_id || item.session_id || item.supplier_id || item.material_id || item.po_id || item.serial;
                    return (
                      <ResultRow
                        key={`${s.key}-${id || idxInSec}`}
                        icon={s.icon}
                        primary={s.primary(item)}
                        secondary={s.secondary(item)}
                        active={fi === activeIdx}
                        onHover={() => setActiveIdx(fi)}
                        onSelect={() => onSelect(fi)}
                        testId={`cmdk-result-${s.key}-${idxInSec}`}
                      />
                    );
                  })}
                </div>
              );
            })}
            </>
          ) : (
            QUICK_LINKS.map((qk, i) => (
              <QuickLinkRow
                key={qk.k}
                label={qk.label}
                active={i === activeIdx}
                onHover={() => setActiveIdx(i)}
                onSelect={() => onSelect(i)}
                testId={`cmdk-quick-${qk.k}`}
              />
            ))
          )}
        </div>

        <FooterHints />
      </div>
    </div>
  );
}
